import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Stack, LinearProgress } from "@mui/material";
import TopBar from "../../../../../component/TopBar";
import MainBox from "../../../container/MainBox";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import SectionLoader from "../../../../../component/SectionLoader";
import EmptyState from "../../../../../component/EmptyState";
import DataTable from "../../../../../component/DataTable";
import { StatusChip, FilterSelect, personName } from "../../../../../component/listing";
import { getReviewCycles, getMyReviews, getReviewsToDo, getAllGoals } from "../../../../../services/performance";
import CycleFormDialog from "./CycleFormDialog";
import { reviewStatusColumn, reviewRatingColumn } from "./reviewColumns";
import PerformanceDashboard from "./Dashboard";
import { useNotify } from "../../../../../component/NotificationProvider";
import { tokens } from "../../../../../theme";

import { pressable } from "../../../../../component/pressableProps";
const CYCLE_TONE = { draft: "muted", active: "info", closed: "success" };
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
const TABLE_SX = { width: "100%", tableLayout: "fixed" };

const pct = (p) => (p?.total ? Math.round(((p.finalized || 0) / p.total) * 100) : 0);

const CYCLE_COLUMNS = [
  { key: "name", label: "Cycle", cellSx: { fontWeight: 600 }, render: (c) => c.name },
  { key: "period", label: "Period", render: (c) => `${fmt(c.periodStart)} – ${fmt(c.periodEnd)}` },
  { key: "status", label: "Status", render: (c) => <StatusChip label={c.status} tone={CYCLE_TONE[c.status]} /> },
  {
    key: "progress",
    label: "Progress",
    render: (c) =>
      c.status === "draft" ? "—" : (
        <Stack direction="row" alignItems="center" spacing={1}>
          <LinearProgress variant="determinate" value={pct(c.progress)} sx={{ flex: 1, height: 6, borderRadius: 3 }} />
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, minWidth: "5.5rem" }}>{c.progress?.finalized || 0}/{c.progress?.total || 0} done</Box>
        </Stack>
      ),
  },
];

const GOAL_COLUMNS = [
  { key: "employee", label: "Employee", cellSx: { fontWeight: 600 }, render: (g) => personName(g.employee) },
  { key: "objective", label: "Objective", render: (g) => g.objective },
  {
    key: "cycle", label: "Cycle", width: "9rem",
    render: (g) =>
      g.reviewCycle?.name
        ? <StatusChip label={g.reviewCycle.name} tone={g.locked?.definition ? "muted" : "info"} />
        : <Box component="span" sx={{ color: tokens.textPlaceholder }}>—</Box>,
  },
  { key: "weight", label: "Weight", width: "5rem", cellSx: { fontVariantNumeric: "tabular-nums" }, render: (g) => g.weight ?? 1 },
  {
    key: "progress", label: "Progress", width: "11rem",
    render: (g) => (
      <Stack direction="row" alignItems="center" spacing={1}>
        <LinearProgress variant="determinate" value={Math.min(100, g.progress || 0)} sx={{ flex: 1, height: 6, borderRadius: 3 }} />
        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, minWidth: "2.5rem" }}>{g.progress || 0}%</Box>
      </Stack>
    ),
  },
];

// "My reviews" and "To review" are the same list read from the two ends of it:
// the only difference is whose name the second column carries.
const reviewColumnsFor = (tab) => [
  { key: "cycle", label: "Cycle", cellSx: { fontWeight: 600 }, render: (r) => r.cycle?.name || "—" },
  { key: "person", label: tab === "mine" ? "Reviewer" : "Employee", render: (r) => personName(tab === "mine" ? r.reviewer : r.employee) },
  reviewStatusColumn,
  reviewRatingColumn(),
];

const TABS = [["overview", "Overview"], ["cycles", "Review Cycles"], ["goals", "Goals"], ["mine", "My Reviews"], ["todo", "To Review"]];

const Performance = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [tab, setTab] = useState("overview");
  const [cycles, setCycles] = useState([]);
  const [mine, setMine] = useState([]);
  const [todo, setTodo] = useState([]);
  const [goals, setGoals] = useState([]);
  // the planning question is nearly always narrower than "everything"
  const [goalCycle, setGoalCycle] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dialog, setDialog] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const [c, m, t, g] = await Promise.all([getReviewCycles(), getMyReviews(), getReviewsToDo(), getAllGoals()]);
      setCycles(Array.isArray(c?.data) ? c.data : []);
      setMine(Array.isArray(m?.data) ? m.data : []);
      setTodo(Array.isArray(t?.data) ? t.data : []);
      setGoals(Array.isArray(g?.data) ? g.data : []);
    } catch (e) { setError(true); notify.error(e?.message || "Failed to load performance data"); }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar
        MainHeading={"Performance"}
        breadcrumbs={[{ label: "Performance" }]}
        actions={
          /* Configuration, not a dashboard — its own screen, reached from here
             rather than buried in the cycle form. */
          <BtnSecondary to="/app/performance/questions" sx={{ borderRadius: "8px" }}>
            Review questions
          </BtnSecondary>
        }
      />
      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.25rem", flexWrap: "wrap", gap: "1rem" }}>
          <Stack direction="row" spacing={0.5} sx={{ background: tokens.surfaceSubtle, p: "0.25rem", borderRadius: "10px" }}>
            {TABS.map(([k, label]) => {
              const count = k === "mine" ? mine.length : k === "todo" ? todo.length : k === "cycles" ? cycles.length : k === "goals" ? goals.length : null;
              return (
                <Box key={k} {...pressable(() => setTab(k))} sx={{ cursor: "pointer", px: "1rem", py: "0.5rem", borderRadius: "8px", fontSize: "0.875rem", fontWeight: 600,
                  background: tab === k ? tokens.surface : "transparent", color: tab === k ? tokens.text : tokens.textMuted, boxShadow: tab === k ? "0 1px 2px rgba(0,0,0,0.06)" : "none" }}>
                  {label}{count != null && <Box component="span" sx={{ color: tokens.textMuted, fontWeight: 500 }}> · {count}</Box>}
                </Box>
              );
            })}
          </Stack>
          {tab === "cycles" && <BtnPrimary onClick={() => setDialog(true)} sx={{ borderRadius: "8px" }}>New cycle</BtnPrimary>}
        </Stack>

        {tab === "overview" ? <PerformanceDashboard onStartReview={() => setDialog(true)} /> : loading ? <SectionLoader /> : error ? <EmptyState variant="error" title="Couldn't load" onRetry={fetchAll} /> : (
          <>
            {tab === "cycles" && (
              <DataTable
                columns={CYCLE_COLUMNS}
                rows={cycles}
                tableSx={TABLE_SX}
                empty={{ title: "No review cycles yet", description: "Create a cycle to kick off appraisals." }}
                onRowClick={(c) => navigate(`/app/performance/cycle/${c._id}`)}
              />
            )}

            {tab === "goals" && (() => {
              const rows = goalCycle
                ? goals.filter((g) => String(g.reviewCycle?._id || g.reviewCycle || "") === goalCycle)
                : goals;
              return (
                <>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: "1rem", flexWrap: "wrap" }}>
                    <FilterSelect
                      value={goalCycle}
                      options={[
                        { value: "", label: "All cycles" },
                        { value: "__none", label: "Not tied to a cycle" },
                        ...cycles.map((c) => ({ value: c._id, label: c.name })),
                      ]}
                      onChange={(e) => setGoalCycle(e.target.value === "__none" ? "" : e.target.value)}
                    />
                    <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
                      {rows.length} goal{rows.length === 1 ? "" : "s"}
                    </Box>
                  </Stack>
                  <DataTable
                    columns={GOAL_COLUMNS}
                    rows={rows}
                    tableSx={TABLE_SX}
                    empty={{ title: "No goals", description: "Goals set on an employee's profile appear here, so you can see who has them before a cycle starts." }}
                    onRowClick={(g) => navigate(`/app/employee-details/${g.employee?._id || g.employee}/goals`)}
                  />
                </>
              );
            })()}

            {(tab === "mine" || tab === "todo") && (
              <DataTable
                columns={reviewColumnsFor(tab)}
                rows={tab === "mine" ? mine : todo}
                tableSx={TABLE_SX}
                empty={{
                  title: tab === "mine" ? "No reviews assigned to you" : "Nothing to review",
                  description: tab === "mine" ? "Your appraisals will appear here." : "Reviews for your reports will appear here.",
                }}
                onRowClick={(r) => navigate(`/app/performance/review/${r._id}`)}
              />
            )}
          </>
        )}
      </MainBox>
      <CycleFormDialog open={dialog} onClose={() => setDialog(false)} onDone={fetchAll} />
    </Box>
  );
};

export default Performance;
